// ============================================================================
// ModalitaBox.jsx  —  FIX_MODALITA_IN_SICUREZZA_v1
//
// Componente autonomo per configurare le "Modalita' organizzative del cantiere"
// (aspetti generali + servizi igienico-assistenziali) direttamente dentro la
// pagina "Sicurezza Azienda" (sezione 8), senza passare da Impostazioni.
//
// Usa la STESSA fonte dati del form in Impostazioni:
//   Firestore: impostazioni/modalita_organizzative_default
//
// Componente isolato: stati e funzioni propri. Non tocca Gestionale.jsx.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { doc, onSnapshot, setDoc } from './firebase-shim';

const theme = {
  bg: "#0f1117",
  surface: "#1a1d27",
  surfaceHover: "#22263a",
  border: "#2a2e45",
  accent: "#f5a623",
  text: "#e8eaf2",
  textMuted: "#7b82a0",
  textDim: "#4a5068",
  success: "#3ecf8e",
  info: "#378ADD",
  warning: "#D4537E",
  error: "#f06060",
};

const inputSt = {
  background: "#0f1117",
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
  color: theme.text,
  padding: "10px 12px",
  fontSize: 14,
  fontFamily: "'DM Sans',sans-serif",
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const MOD_DEFAULT_VUOTO = {
  aspetti: {
    recinzione:           { descrizione: "", vedasiAllegato: false },
    viabilita:            { descrizione: "", vedasiAllegato: false },
    depositoMateriali:    { descrizione: "", vedasiAllegato: false },
    depositoAttrezzature: { descrizione: "", vedasiAllegato: false },
    altro:                { descrizione: "", vedasiAllegato: false },
  },
  servizi: {
    prontoSoccorso: { opzione: "", chi: "", note: "" },
    wc:             { opzione: "", chi: "", note: "" },
    lavabi:         { opzione: "", chi: "", note: "" },
    docce:          { opzione: "", chi: "", note: "" },
    spogliatoi:     { opzione: "", chi: "", note: "" },
    altro:          { opzione: "", chi: "", note: "" },
  },
};

export default function ModalitaBox() {
  const [modDefault, setModDefault] = useState(MOD_DEFAULT_VUOTO);
  const [modDefaultOriginale, setModDefaultOriginale] = useState(MOD_DEFAULT_VUOTO);
  const [modDefaultDirty, setModDefaultDirty] = useState(false);
  const modDefaultSaveTimer = useRef(null);
  // FIX_NO_OVERWRITE: mentre l'utente sta scrivendo, onSnapshot NON deve
  // reiniettare i dati nel campo (causava il "torna indietro" delle lettere).
  const stoScrivendo = useRef(false);
  const fineScritturaTimer = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "impostazioni", "modalita_organizzative_default"), snap => {
      // Se l'utente sta scrivendo proprio ora, ignoro l'aggiornamento da Firebase
      if (stoScrivendo.current) return;
      if (snap.exists()) {
        const data = snap.data();
        const merged = {
          aspetti: { ...MOD_DEFAULT_VUOTO.aspetti, ...(data.aspetti || {}) },
          servizi: { ...MOD_DEFAULT_VUOTO.servizi, ...(data.servizi || {}) },
        };
        setModDefault(merged);
        setModDefaultOriginale(merged);
        setModDefaultDirty(false);
      }
    });
    return () => {
      try { unsub && unsub(); } catch(e){}
      if (fineScritturaTimer.current) clearTimeout(fineScritturaTimer.current);
    };
  }, []);

  const salvaModDefault = async (data) => {
    try {
      await setDoc(doc(db, "impostazioni", "modalita_organizzative_default"), { ...data, updatedAt: new Date().toISOString() });
    } catch(e) { alert("❌ " + e.message); }
  };

  // Segna che l'utente sta scrivendo; sblocca onSnapshot 1.5s dopo l'ultima digitazione
  const segnaScrittura = () => {
    stoScrivendo.current = true;
    if (fineScritturaTimer.current) clearTimeout(fineScritturaTimer.current);
    fineScritturaTimer.current = setTimeout(() => { stoScrivendo.current = false; }, 1500);
  };

  const aggiornaAspetto = (key, campo, valore) => {
    segnaScrittura();
    const nuovo = { ...modDefault, aspetti: { ...modDefault.aspetti, [key]: { ...modDefault.aspetti[key], [campo]: valore } } };
    setModDefault(nuovo);
    setModDefaultDirty(true);
    if (modDefaultSaveTimer.current) clearTimeout(modDefaultSaveTimer.current);
    modDefaultSaveTimer.current = setTimeout(() => salvaModDefault(nuovo), 800);
  };

  const aggiornaServizio = (key, campo, valore) => {
    segnaScrittura();
    const nuovo = { ...modDefault, servizi: { ...modDefault.servizi, [key]: { ...modDefault.servizi[key], [campo]: valore } } };
    setModDefault(nuovo);
    setModDefaultDirty(true);
    if (modDefaultSaveTimer.current) clearTimeout(modDefaultSaveTimer.current);
    modDefaultSaveTimer.current = setTimeout(() => salvaModDefault(nuovo), 800);
  };

  const cardSt = { background:theme.surface, border:`1px solid ${theme.border}`, borderRadius:10, padding:16 };

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 700, fontSize: 15 }}>🔧 Modalità organizzative — default Bertoc</div>
          <button className="btn btn-ghost btn-sm" disabled={!modDefaultDirty}
            onClick={() => {
              if (!window.confirm("Annullare le modifiche e ripristinare l'ultimo salvataggio?")) return;
              setModDefault(modDefaultOriginale);
              setModDefaultDirty(false);
              salvaModDefault(modDefaultOriginale);
            }}
            style={{ opacity: modDefaultDirty ? 1 : 0.4 }}
          >↶ Annulla modifiche</button>
        </div>
        <div style={{ fontSize: 13, color: theme.textMuted }}>
          Imposta i valori predefiniti proposti quando generi un POS. Potrai modificarli per ogni cantiere singolarmente. Salvataggio automatico.
        </div>
      </div>

      {/* ASPETTI GENERALI */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Aspetti generali del cantiere
        </div>
        {[
          { key: "recinzione",           label: "Recinzione area di cantiere" },
          { key: "viabilita",            label: "Viabilità ed accessi di cantiere" },
          { key: "depositoMateriali",    label: "Aree di deposito materiali" },
          { key: "depositoAttrezzature", label: "Aree di deposito attrezzature" },
          { key: "altro",                label: "Altro (specificare)" },
        ].map(r => {
          const val = modDefault.aspetti[r.key] || { descrizione: "", vedasiAllegato: false };
          return (
            <div key={r.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{r.label}</div>
              <textarea style={{ ...inputSt, minHeight: 50, resize: "vertical", marginBottom: 6 }}
                placeholder="Descrizione (es. Si rimanda al PSC, A cura impresa esecutrice, ...)"
                value={val.descrizione}
                onChange={e => aggiornaAspetto(r.key, "descrizione", e.target.value)} />
              <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                <input type="checkbox" checked={val.vedasiAllegato}
                  onChange={e => aggiornaAspetto(r.key, "vedasiAllegato", e.target.checked)} />
                Vedasi documentazione allegata
              </label>
            </div>
          );
        })}
      </div>

      {/* SERVIZI IGIENICO-ASSISTENZIALI */}
      <div style={{ ...cardSt, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.accent, textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
          Servizi igienico-assistenziali
        </div>
        {[
          { key: "prontoSoccorso", label: "Cassetta Pronto Soccorso" },
          { key: "wc",             label: "WC" },
          { key: "lavabi",         label: "Lavabi" },
          { key: "docce",          label: "Docce" },
          { key: "spogliatoi",     label: "Spogliatoi" },
          { key: "altro",          label: "Altro (specificare)" },
        ].map(r => {
          const val = modDefault.servizi[r.key] || { opzione: "", chi: "", note: "" };
          return (
            <div key={r.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${theme.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{r.label}</div>
              {[
                { v: "committente",    l: "Messo a disposizione da", chiVis: true },
                { v: "non_pertinente", l: "Non pertinente",          chiVis: false },
                { v: "accordo",        l: "Organizzato in accordo con", chiVis: true },
                { v: "impresa",        l: "Da installarsi a cura impresa esecutrice", chiVis: false },
              ].map(o => (
                <label key={o.v} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, marginBottom: 4, cursor: "pointer" }}>
                  <input type="radio" name={`opt_${r.key}`} checked={val.opzione === o.v}
                    onChange={() => aggiornaServizio(r.key, "opzione", o.v)} />
                  <span>{o.l}</span>
                  {o.chiVis && val.opzione === o.v && (
                    <input style={{ ...inputSt, width: 280, padding: "4px 8px", marginLeft: 8 }}
                      placeholder="Specificare chi..."
                      value={val.chi}
                      onClick={e => e.stopPropagation()}
                      onChange={e => aggiornaServizio(r.key, "chi", e.target.value)} />
                  )}
                </label>
              ))}
              <input style={{ ...inputSt, marginTop: 6 }}
                placeholder="Note (opzionale)"
                value={val.note}
                onChange={e => aggiornaServizio(r.key, "note", e.target.value)} />
            </div>
          );
        })}
      </div>

      <div style={{ fontSize: 11, color: theme.textDim, textAlign: "center", padding: "8px 0" }}>
        {modDefaultDirty ? "💾 Salvataggio in corso..." : "✓ Tutti i dati salvati"}
      </div>
    </div>
  );
}

